<template>
  <section class="payment-methods__wrapper mt-5">
    <h1 class="mb-4">
      Способы оплаты
    </h1>

    <navigation-tabs class="mb-4">
      <template #middle>
        <span class="payment-methods__hint">
          Сохранённые карты для пополнения баланса и автосписания за тариф
        </span>
      </template>
    </navigation-tabs>

    <loader v-if="loading" />

    <template v-else>
      <!-- Пусто -->
      <payment-methods-empty
        v-if="!methods.length"
        :busy="addingCard"
        @add="onAddCard"
      />

      <!-- Есть карты -->
      <div v-else class="pm-card">
        <div class="pm-section__header">
          <span class="pm-section__title">Основная карта</span>
          <a
            v-if="defaultMethod && canUseBilling"
            class="pm-delete-link"
            @click="askDelete(defaultMethod)"
          >/ удалить</a>
        </div>

        <div class="pm-row">
          <!-- Основная карта -->
          <payment-method-card
            v-if="defaultMethod"
            :method="defaultMethod"
            :autopay-busy="autopayBusy"
            @toggle-autopay="onToggleAutopay"
          />

          <!-- На телефоне ссылка в заголовке слишком мелкая для тапа — отдельная кнопка -->
          <div v-if="defaultMethod && canUseBilling" class="pm-delete-mobile">
            <v-btn
              class="pm-delete-btn"
              block
              outlined
              depressed
              color="#EB5757"
              @click="askDelete(defaultMethod)"
            >
              <v-icon left>
                mdi-trash-can-outline
              </v-icon>
              Удалить карту
            </v-btn>
          </div>

          <!-- Добавление резервной -->
          <div v-if="canUseBilling" class="pm-add-block">
            <v-btn
              class="pm-add-btn"
              x-large
              depressed
              :loading="addingCard"
              :disabled="addingCard"
              @click="onAddCard"
            >
              <v-icon left>
                mdi-plus
              </v-icon>
              Добавить карту
            </v-btn>
            <p class="pm-add-block__hint">
              Добавляете резервные способы оплаты, на случай нехватки средств на основной карте
            </p>
          </div>
        </div>

        <!-- Резервные способы -->
        <template v-if="backupMethods.length">
          <div class="pm-section__header pm-section__header_backup">
            <span class="pm-section__title">Резервные способы</span>
          </div>

          <v-row class="pm-row_backup">
            <v-col
              v-for="item in backupMethods"
              :key="item.id"
              cols="12"
            >
              <payment-method-card
                :method="item"
                backup
                :busy="busyId === item.id"
                @set-default="onSetDefault(item)"
                @delete="askDelete(item)"
              />
            </v-col>
          </v-row>
        </template>
      </div>
    </template>

    <primary-confirm-dialog
      v-model="deleteDialog"
      title="Удалить способ оплаты?"
      cancel-text="Вернуться назад"
      :loading="deleting"
      @close="methodToDelete = null"
    >
      <template #subtitle>
        Карта <b>•••• {{ methodToDelete && methodToDelete.last4 }}</b> будет удалена без возможности восстановления.
        <template v-if="nextDefaultAfterDelete">
          Основной станет карта <b>•••• {{ nextDefaultAfterDelete.last4 }}</b> — автоплатёж перейдёт на неё.
        </template>
        <template v-else-if="methodToDelete && methodToDelete.isDefault">
          Других карт не останется: списывать за тариф будет не с чего.
        </template>
      </template>
      <template #confirm-btn>
        <large-btn
          class="primary-confirm-dialog_btn primary-confirm-dialog_btn__confirm pm-delete-confirm"
          color="red"
          height="64px"
          :uppercase="false"
          block
          :loading="deleting"
          @click="confirmDelete"
        >
          <v-icon
            left
            size="32"
            color="#fff"
          >
            mdi-trash-can-outline
          </v-icon>
          Удалить
        </large-btn>
      </template>
    </primary-confirm-dialog>
  </section>
</template>

<script>
  import { mapState } from 'vuex'
  import Loader from '@/components/loader/Loader'
  import SnackbarMessage from '@/mixins/snackbar-message'
  import NavigationTabs from '@/components/billing/NavigationTabs.vue'
  import PaymentMethodCard from '@/components/billing/PaymentMethodCard.vue'
  import PaymentMethodsEmpty from '@/components/billing/PaymentMethodsEmpty.vue'
  import PrimaryConfirmDialog from '@/components/ui/dialogs/PrimaryConfirmDialog.vue'
  import LargeBtn from '@/components/ui/buttons/LargeBtn.vue'
  import billingApi from '@/api/billing'
  import { websocketService } from '@/services/WebsocketService'
  import { getPaymentAccountChannel } from '@/util/websocket-channels'
  import { CRM_BILLING_PAYMENT_METHOD_STATUS_CHANGED } from '@/constants/websocket-events'

  export default {
    name: 'PaymentMethods',
    components: { NavigationTabs, PaymentMethodCard, PaymentMethodsEmpty, PrimaryConfirmDialog, LargeBtn, Loader },
    mixins: [SnackbarMessage],
    data () {
      return {
        loading: false,
        methods: [],
        busyId: null,
        autopayBusy: false,
        deleteDialog: false,
        deleting: false,
        methodToDelete: null,
        addingCard: false,
      }
    },
    computed: {
      ...mapState('billing', ['paymentAccount']),
      defaultMethod () {
        return this.methods.find((m) => m.isDefault) || null
      },
      backupMethods () {
        return this.methods.filter((m) => !m.isDefault)
      },
      // Бэк отдаёт бейдж основной самой старой из оставшихся — показываем ту же карту
      nextDefaultAfterDelete () {
        if (!this.methodToDelete || !this.methodToDelete.isDefault) return null

        return this.backupMethods.slice().sort((a, b) => a.id - b.id)[0] || null
      },
    },
    async created () {
      this.load()
      try {
        await this.$store.dispatch('billing/fetchPaymentAccount')
        this.listen()
      } catch (err) {
        // баланс/автоплатёж не критичны для списка карт
      }
    },
    beforeDestroy () {
      const channel = this.channel()
      if (channel) {
        channel.stopListening(CRM_BILLING_PAYMENT_METHOD_STATUS_CHANGED, this.onStatusChanged)
      }
    },
    methods: {
      // Локально echo-server поднят без авторизации — там канал публичный
      channel () {
        if (!this.paymentAccount.id) return null
        const echo = websocketService.getEcho()
        const name = getPaymentAccountChannel(this.paymentAccount.id)

        return process.env.VUE_APP_MODE === 'local' ? echo.channel(name) : echo.private(name)
      },
      listen () {
        const channel = this.channel()
        if (channel) {
          channel.listen(CRM_BILLING_PAYMENT_METHOD_STATUS_CHANGED, this.onStatusChanged)
        }
      },
      // Статус меняет провайдер (вебхук или сверка) — перечитываем весь список:
      // вместе со статусом мог переехать флаг основной карты
      onStatusChanged () {
        this.fetchMethods()
      },
      // Сюда же возвращаются с формы привязки карты: сверяем её с ЮKassa,
      // не дожидаясь вебхука, иначе карта не появится в списке сразу.
      // Просмотрщик карты не привязывает, а сверка закрыта правом manage — ему не дёргаем
      async load () {
        this.loading = true
        if (this.canUseBilling) {
          try {
            await billingApi.checkPendingCard()
          } catch (err) {
            this.showErrorFromResponse(err)
          }
        }

        await this.fetchMethods()
      },
      async fetchMethods () {
        this.loading = true
        try {
          this.methods = await billingApi.fetchPaymentMethods()
        } catch (err) {
          this.showErrorFromResponse(err)
        } finally {
          this.loading = false
        }
      },
      async onToggleAutopay (value) {
        const card = this.defaultMethod
        if (!card) return

        this.autopayBusy = true
        card.autopayEnabled = value
        try {
          if (value) {
            await billingApi.enableAutopay(card.id)
          } else {
            await billingApi.disableAutopay(card.id)
          }
        } catch (err) {
          card.autopayEnabled = !value
          this.showErrorFromResponse(err)
        } finally {
          this.autopayBusy = false
        }
      },
      async onSetDefault (item) {
        if (item.isDefault || this.busyId === item.id) return
        this.busyId = item.id
        try {
          await billingApi.setDefaultPaymentMethod(item.id)
          // default эксклюзивен: сервер снимает старый — перечитываем весь список
          await this.fetchMethods()
        } catch (err) {
          this.showErrorFromResponse(err)
        } finally {
          this.busyId = null
        }
      },
      async onAddCard () {
        this.addingCard = true
        try {
          const { confirmationUrl } = await billingApi.bindCard()
          if (confirmationUrl) {
            window.location.href = confirmationUrl
          } else {
            this.showInfoMessage('Привязка обрабатывается, обновите страницу позже')
          }
        } catch (err) {
          this.showErrorFromResponse(err)
        } finally {
          this.addingCard = false
        }
      },
      askDelete (item) {
        this.methodToDelete = item
        this.deleteDialog = true
      },
      async confirmDelete () {
        const item = this.methodToDelete
        if (!item) return
        this.deleting = true
        try {
          this.methods = await billingApi.deletePaymentMethod(item.id)
          this.deleteDialog = false
          this.methodToDelete = null
        } catch (err) {
          this.showErrorFromResponse(err)
        } finally {
          this.deleting = false
        }
      },
    },
  }
</script>

<style lang="less" scoped>
.payment-methods {
  &__wrapper {
    padding-left: 4px;
    padding-right: 4px;
  }

  &__hint {
    color: #949191;
    font-size: 14px;

    // на узких экранах место отдаём кнопкам вкладок
    .media-small-pc({
      display: none;
    });
  }
}

// тему Vuetify не перебить без !important: без него текст кнопки остаётся чёрным
.pm-delete-confirm {
  color: #fff !important;

  // Vuetify зажимает иконку кнопки в бокс 18×18 — размер глифа задаёт size,
  // а без сброса бокса он наезжает на текст
  ::v-deep .v-icon {
    width: auto !important;
    height: auto !important;
  }
}

.pm-card {
  background-color: #fff;
  border: 1px solid #c4cdd9;
  border-radius: 8px;
  padding: 24px;

  .media-xs({
    padding: 16px;
  });
}

.pm-section {
  &__header {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 12px;

    &_backup {
      margin-top: 28px;
    }
  }

  &__title {
    font-size: 18px;
    color: #000;

    .media-xs({
      font-size: 16px;
    });
  }
}

.pm-delete-link {
  font-size: 15px;
  color: #bab7b7;
  cursor: pointer;

  // на телефоне вместо неё отдельная кнопка под картой
  .media-xs({
    display: none;
  });

  &:hover {
    color: #eb5757;
  }
}

.pm-delete-mobile {
  display: none;

  .media-xs({
    display: block;
  });
}

.pm-delete-btn.v-btn {
  border-radius: 8px;
  font-weight: 500;
  text-transform: none;
  letter-spacing: normal;
}

.pm-row {
  display: grid;
  // две колонки, пока каждой хватает ширины на бейдж, номер карты и переключатель;
  // иначе сетка сама переходит в одну
  grid-template-columns: repeat(auto-fit, minmax(530px, 1fr));
  gap: 16px;

  // на планшете и уже двух колонок всё равно не выходит, а фиксированный
  // минимум колонки к этому моменту уже шире контейнера
  .media-sm({
    grid-template-columns: minmax(0, 1fr);
  });
}

.pm-row_backup.row {
  // 16px между колонками, как gap в ряду выше; дефолтный gutter Vuetify — 24px.
  // Отрицательные поля гасят падинги крайних колонок, чтобы ряд не сузился
  margin: 4px -8px 0;

  > [class*='col'] {
    padding: 8px;
  }

  // Половина ряда — с той ширины, где колонке достаётся минимум 530px:
  // сайдбар 260 + падинги контейнера и карточки съедают ~340. Готовый lg (1264)
  // для этого тесен — на нём содержимое карточки уже не встаёт в строку
  @media (min-width: 1400px) {
    > [class*='col'] {
      flex: 0 0 50%;
      max-width: 50%;
    }
  }
}

.pm-add-block {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  min-width: 0;
  background-color: #f1f3f6;
  border-radius: 6px;
  padding: 20px 24px;

  .media-xs({
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    padding: 16px;
  });

  &__hint {
    flex: 1 1 220px;
    margin: 0;
    font-size: 15px;
    color: rgba(0, 0, 0, 0.87);

    // блок здесь колонка — базис 220px стал бы высотой, а не шириной
    .media-xs({
      flex: 0 0 auto;
      font-size: 14px;
    });
  }
}

.pm-add-btn.v-btn {
  background-color: #4caf50 !important;
  color: #fff !important;
  border-radius: 8px;
  font-weight: 600;
  text-transform: none;
  letter-spacing: normal;

  .media-xs({
    width: 100%;
  });
}
</style>
